/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { makeObservable, observable, computed } from "mobx"
import { htmlToTextTransform } from "@lib/html"

import { BaseModel } from "./base.model"
import { PAGE_SIZE } from "./blog.model"

import type { AxiosError } from "axios"
import type { LanguageISO } from "@providers"
import type { PostModel } from "@serializers/article"
import type { Localization } from "@serializers/localization"
import type { BlogModel, PostNotFoundHTTPErrorPaylod } from "./blog.model"

interface SaferDatingModelInterface
  extends Pick<
    BlogModel,
    | "templates"
    | "posts"
    | "pagination"
    | "postDetails"
    | "existsGridPosts"
    | "postsChunks"
    | "articleBanner"
    | "relatedPostsBySlug"
    | "getTemplate"
    | "getInitialData"
    | "getPage"
    | "getPost"
    | "getPosts"
    | "getPostsBySlug"
  > {}

export class SaferDatingModel
  extends BaseModel
  implements SaferDatingModelInterface {
  // @ts-ignore
  constructor(params) {
    super(params)
    makeObservable(this)
  }

  @observable templates: Localization[] = []

  @observable posts: PostModel[] = []
  @observable pagination = {
    page: 0,
    pageSize: 0,
    pageCount: 0,
    rowCount: 0,
  }
  @observable postDetails: PostModel | null = null
  @observable featuredPost?: PostModel
  @observable branchLinks: Record<string, string> = {}

  // Languages this page exists in, used by its own footer switcher so it can
  // differ from the site-wide list.
  @observable availableLanguages: LanguageISO[] = []

  @computed get existsGridPosts() {
    return this.posts.length > 0
  }

  @computed get postsChunks() {
    const chunkLength = this.pagination.pageSize || PAGE_SIZE
    return this.posts.reduce((result, _, index, postsArray) => {
      if (index % chunkLength === 0) {
        result.push(postsArray.slice(index, index + chunkLength))
      }
      return result
    }, [] as PostModel[][])
  }

  @computed get articleBanner() {
    return this.postDetails?.banner ?? null
  }

  @observable relatedPostsBySlug = [] as PostModel[]

  hydrate = (data: unknown) => {
    // @ts-ignore
    this.applyData({ posts: data?.posts ?? [] })
    this.applyData({
      // @ts-ignore
      pagination: data?.pagination ?? {
        page: 0,
        pageSize: 0,
        pageCount: 0,
        rowCount: 0,
      },
    })
    // @ts-ignore
    this.applyData({ postDetails: data?.postDetails })
    // @ts-ignore
    this.applyData({ featuredPost: data?.featuredPost })
    // @ts-ignore
    this.applyData({ branchLinks: data?.branchLinks })
    // @ts-ignore
    this.applyData({ templates: data?.templates })
    // @ts-ignore
    this.applyData({ relatedPostsBySlug: data?.relatedPostsBySlug })
    // @ts-ignore
    this.applyData({ availableLanguages: data?.availableLanguages ?? [] })
  }

  serialize = () => {
    return {
      posts: this.posts,
      pagination: this.pagination,
      postDetails: this.postDetails,
      templates: this.templates,
      relatedPostsBySlug: this.relatedPostsBySlug,
      featuredPost: this.featuredPost,
      branchLinks: this.branchLinks,
      availableLanguages: this.availableLanguages,
    }
  }

  getTemplate = (key: string, simpleText = true) => {
    const result = this.templates.find((template) => template.key === key)
    if (result) {
      return simpleText ? htmlToTextTransform(result.template) : result.template
    }

    return ""
  }

  getInitialData = async () => {
    await this.getPage()
    await this.getPosts({ page: 1 })
  }

  getPage = async () => {
    const {
      templates,
      featuredPost,
      branchLinks,
      availableLanguages,
    } = await this.Providers.SaferDatingProvider.getSaferDatingPage(
      this.Models.GlobalModel.lang,
    )

    this.applyData({
      templates,
      featuredPost,
      branchLinks,
      availableLanguages,
    })
  }

  getPost = async (slug: string) => {
    if (this.postDetails) return
    try {
      await this.getPage()
      await this.getPostsBySlug(slug)

      const lang = this.Models.GlobalModel.lang
      const post = await this.Providers.SaferDatingProvider.getPost(
        lang,
        slug,
        this.Models.GlobalModel.countryCode,
      )

      if (this.Models.BlogModel.isAmp) {
        const {
          description,
          elements,
        } = await this.Models.BlogModel.processArticleForAMP(post)
        post.description = description
        post.elements = elements
      }

      return this.applyData({
        postDetails: post,
      })
    } catch (err) {
      const error = err as AxiosError<PostNotFoundHTTPErrorPaylod>
      if (error?.response?.status === 404) {
        if (
          !this.relatedPostsBySlug.length ||
          error?.response?.data?.throw_error
        ) {
          throw err
        }
      }
    }
  }

  getPosts = async ({ page = 1, pageSize = PAGE_SIZE }) => {
    const {
      data,
      pagination,
    } = await this.Providers.SaferDatingProvider.getPosts({
      lang: this.Models.GlobalModel.lang,
      page,
      pageSize,
      countryCode: this.Models.GlobalModel.countryCode,
    })
    // @ts-ignore
    this.applyData({ posts: this.posts.concat(data), pagination })
  }

  getPostsBySlug = async (slug: string) => {
    if (slug) {
      try {
        const {
          posts,
        } = await this.Providers.SaferDatingProvider.getPostsBySlug(
          slug,
          this.Models.GlobalModel.countryCode,
        )
        this.applyData({ relatedPostsBySlug: posts })
      } catch (error) {
        throw error
      }
    }

    return
  }
}
